import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import formatPhone from '../utils/formatPhone'; // Assumindo que você tem este utilitário
import { getEmpresaAtual } from './api';
import {
  coresHex, hexParaRgb,
} from '../styles/styleUtils';

// Converte um hex de coresHex em rgb() 0-1 do pdf-lib (que não entende var()/hex).
const rgbHex = (hex) => {
  const { r, g, b } = hexParaRgb(hex);
  return rgb(r / 255, g / 255, b / 255);
};

/**
 * Carrega todas as imagens necessárias de uma vez.
 * @param {PDFDocument} pdfDoc
 * @returns {Promise<Object>} Um objeto com todas as imagens embutidas.
 */
const loadAllImages = async (pdfDoc) => {
  const fetchAndEmbed = async (path) => {
    try {
      const imageBytes = await fetch(path).then((res) => res.arrayBuffer());
      return await pdfDoc.embedPng(imageBytes);
    } catch (error) {
      console.error(`Falha ao carregar a imagem: ${path}`, error);
      // Retorna um placeholder ou null para não quebrar a execução
      return null;
    }
  };

  const [logoArasol, logoWeg, sistemaFoto] = await Promise.all([
    fetchAndEmbed('/logo.png'), // <-- AJUSTE O CAMINHO
    fetchAndEmbed('/logoweg.png'), // <-- AJUSTE O CAMINHO
    fetchAndEmbed('/sistemafoto.png'), // <-- AJUSTE O CAMINHO
  ]);

  return { logoArasol, logoWeg, sistemaFoto };
};


/**
 * Desenha a Página 1 da proposta.
 * @param {PDFDocument} pdfDoc
 * @param {Object} context
 * @param {Object} proposta
 */
const drawPage1 = (pdfDoc, context, proposta) => {
  const page = pdfDoc.addPage([context.width, context.height]);
  const {
    width, height, fonts, colors, images, empresa,
  } = context;

  // Logo AraSol (centralizada como no código original)
  if (images.logoArasol) {
    const logoDims = images.logoArasol.scale(0.5);
    const centerX = (width - logoDims.width) / 2;
    page.drawImage(images.logoArasol, {
      x: centerX,
      y: height - logoDims.height - 50,
      width: logoDims.width,
      height: logoDims.height,
    });
  }

  // Logo Weg
  if (images.logoWeg) {
    const logoWegDims = images.logoWeg.scale(0.8);
    const centerX = (width - logoWegDims.width) / 2;
    page.drawImage(images.logoWeg, {
      x: centerX,
      y: height - logoWegDims.height - 230,
      width: logoWegDims.width,
      height: logoWegDims.height,
    });
  }

  // Título Centralizado
  const title = 'Proposta Comercial Serviços:';
  const titleWidth = fonts.bold.widthOfTextAtSize(title, 24);
  page.drawText(title, {
    x: (width - titleWidth) / 2,
    y: height / 2 + 50,
    font: fonts.bold,
    size: 24,
    color: colors.arasolBlue,
  });

  // Tabela de Informações
  let currentY = height / 2 - 50;
  const infoStartX = width / 2 - 250;
  const labelWidth = fonts.bold.widthOfTextAtSize('Responsável Comercial: ', 18);

  const drawInfoRow = (label, value) => {
    page.drawText(label, { x: infoStartX, y: currentY, font: fonts.bold, size: 18, color: colors.arasolBlue });
    page.drawText(value, {
      x: infoStartX + labelWidth,
      y: currentY,
      font: fonts.regular,
      size: 18,
      color: colors.black,
    });
    currentY -= 25;
  };

  drawInfoRow('Cliente:', proposta.cliente.nome);
  drawInfoRow('Contato:', formatPhone(proposta.cliente.telefone));
  drawInfoRow('Data:', format(new Date(proposta.data), 'dd/MM/yyyy', { locale: ptBR }));
  drawInfoRow('Consultor:', proposta.user.nome);
  drawInfoRow('Contato:', formatPhone(proposta.user.telefone));
  currentY -= 15;
  drawInfoRow('Nosso número:', formatPhone(empresa?.telefone || '3432491158'));
  drawInfoRow('Pós-vendas:', formatPhone(empresa?.telefone || '34999875450'));

  // Rodapé
  const footerY = 150;
  const centerText = (text, size, y) => {
    const textWidth = fonts.bold.widthOfTextAtSize(text, size);
    page.drawText(text, { x: (width - textWidth) / 2, y, font: fonts.bold, size, color: colors.arasolBlue });
  };

  centerText(empresa?.nome || 'ARASOL Energia Fotovoltaica', 14, footerY);
  centerText(empresa?.endereco || 'Araguari - MG / Ribeirão Preto - SP', 14, footerY - 25);

  page.drawText('Visite nossas mídias sociais:', { x: 30, y: 110, size: 11, color: colors.arasolBlue });
  page.drawText(`Site: ${empresa?.site || 'www.arasolenergiafotovoltaica.com.br'}`, { x: 30, y: 95, size: 11, color: colors.arasolBlue });
  page.drawText(`Instagram: ${empresa?.instagram || '@arasolenergiafotovoltaica'}`, { x: 30, y: 80, size: 11, color: colors.arasolBlue });
};

/**
 * Desenha a Página 2 da proposta.
 * @param {PDFDocument} pdfDoc
 * @param {Object} context
 */
const drawPage2 = (pdfDoc, context) => {
  const page = pdfDoc.addPage([context.width, context.height]);
  const { width, height, fonts, colors, images } = context;

  const drawSection = (title, content, titleY, contentY, contentHeight) => {
    page.drawRectangle({ x: 30, y: titleY - 4, width: width - 60, height: 25, color: colors.arasolBlue });
    const titleWidth = fonts.bold.widthOfTextAtSize(title, 14);
    page.drawText(title, { x: (width - titleWidth) / 2, y: titleY, size: 14, font: fonts.bold, color: colors.white });

    page.drawRectangle({
      x: 31,
      y: contentY - contentHeight,
      width: width - 62,
      height: contentHeight,
      borderColor: colors.arasolBlue,
      borderWidth: 2,
    });
    page.drawText(content, { x: 35, y: contentY - 18, size: 11, color: colors.arasolBlue, lineHeight: 15 });
  };

  // Cabeçalho Principal
  page.drawRectangle({ x: 30, y: height - 60, width: width - 60, height: 30, color: colors.arasolBlue });
  page.drawText('1 | Porque é importante cuidar do seu sistema fotovoltaico?', {
    x: 40,
    y: height - 50,
    size: 14,
    font: fonts.bold,
    color: colors.white,
  });

  if (images.sistemaFoto) {
    const imgDims = images.sistemaFoto.scale(0.7);
    page.drawImage(images.sistemaFoto, { x: 90, y: 550, width: imgDims.width, height: imgDims.height });
  }

  // Seções de Texto
  const textModulo = `O Módulo fotovoltaico fica exposto a um ambiente extremo, estando sujeito a ventos de até 120 km/h,\nchuvas de granizo que culminam no acúmulo de sujeiras que podem ocasionar uma redução na produção \nde energia do seus sitema de até 25% dependendo da região e do nível de acúmulo.\nOs ventos e oscilações de temperatura ao longo do dia, podem gerar também a necessidade de reaperto \ndos grampos de fixação dos painéis fotovoltaicos em função do esforço mecânico exercido sobre eles.`;
  drawSection('Módulo Fotovoltaico', textModulo, 506, 495, 90);

  const textInversor = `O inversor fotovoltaico é o cérebro do seu sistema! Ele é responsável, pela proteção do sistema fotovoltaico,\nconverter a energia produzida pelo sistema para o padrão utilizado no imóvel, monitorar sua produção e \nfornecer as informações para acompanhamento da performance do seu sistema.\nCom o passar do tempo, podem ocorrer mudanças no seu sinal de internet e queima de disjuntores e \ndemais aparatos de proteção que precisam ser substituídos para garantir o correto funcionamento do seu \ninversor, bem como o reaperto das conexões elétricas em função do aquecimento durante sua operação.`;
  drawSection('Inversor On-grid', textInversor, 367, 355, 100);

  const textMonitoramento = `O sistema de monitoramento é a única ferramenta, além da tela do inversor, na qual é possível acompanhar \na geração total do seu sistema. Em paralelo, pelo app ainda é possível segmentar a produção por dia, \nmês ou ano, além de informar alertas de falha e erros no sistema.\nPor isso é fundamental que seu sistema possua uma conexão estável e contínua, para que possamos\nauxiliá-lo da melhor forma possível e também, para que a produção mensal do sistema possa ser verificada \nde forma acessível e simplificada.`;
  drawSection('Sistema de Monitoramento e pós-vendas:', textMonitoramento, 237, 225, 120);
};

/**
 * Desenha a Página 3 da proposta, com base no estilo das páginas anteriores.
 * @param {PDFDocument} pdfDoc
 * @param {Object} context
 * @param {Object} proposta
 */
const drawPage3 = (pdfDoc, context, proposta) => {
  const page = pdfDoc.addPage([context.width, context.height]);
  const {
    width, height, fonts, colors, margin, empresa,
  } = context;
  let currentY = height - margin - 20;

  // Cabeçalho da página
  page.drawRectangle({
    x: 30,
    y: currentY,
    width: width - 60,
    height: 30,
    color: colors.arasolBlue, // Cor de fundo azul
  });

  page.drawText('2 | Descrição dos Serviços', {
    x: 40,
    y: currentY + 10,
    size: 14,
    font: fonts.bold,
    color: rgb(1, 1, 1), // Cor de texto branca
  });

  currentY -= 30;

  page.drawText(
    'Conforme solicitado, segue a lista dos serviços de manutenção solicitados para que possamos manter\nseu sistema seguro e performando com excelência!',
    {
      x: margin,
      y: currentY,
      font: fonts.regular,
      size: 10,
      color: colors.black,
      lineHeight: 12,
    }
  );
  currentY -= 50;

  // Tabela de Serviços
  const tableTop = currentY;
  const colWidths = [40, 255, 80, 50, 80];
  const tableWidth = colWidths.reduce((a, b) => a + b, 0);

  // Cabeçalho da Tabela
  page.drawRectangle({ x: margin, y: tableTop, width: tableWidth, height: 20, color: colors.amareloclaro });
  let currentX = margin;
  ['Item', 'Descrição', 'Valor unit.', 'Qtde', 'Valor Total'].forEach((header, i) => {
    page.drawText(header, { x: currentX + 5, y: tableTop + 5, font: fonts.bold, size: 10, color: colors.black });
    currentX += colWidths[i];
  });
  currentY -= 5; // Ajuste fino para a primeira linha

  // Linhas da Tabela
  proposta.servicos.forEach((servico, index) => {
    currentY -= 25; // Altura da linha
    const valorTotal = servico.valor * servico.quantidade;
    const row = [
      (index + 1).toString(),
      servico.nome,
      `R$ ${servico.valor.toFixed(2)}`,
      servico.quantidade.toString(),
      `R$ ${valorTotal.toFixed(2)}`,
    ];
    currentX = margin;
    row.forEach((cell, i) => {
      page.drawText(cell, { x: currentX + 5, y: currentY + 5, font: fonts.regular, size: 10, color: colors.black });
      currentX += colWidths[i];
    });
    page.drawLine({
      start: { x: margin, y: currentY },
      end: { x: margin + tableWidth, y: currentY },
      thickness: 0.5,
      color: rgb(0.8, 0.8, 0.8),
    });
  });
  currentY -= 50;

  // Observação
  if (proposta.obs) {
      currentY += 40;
      // Define uma altura de linha menor (bem juntinha) para o texto da observação
      const lineHeight = 10; 
      const observationTextSize = 9;

      // Divide o texto em um array de linhas usando o caractere de quebra de linha (\n)
      const lines = proposta.obs.split('\n');

      // Itera sobre cada linha e a desenha no PDF
      lines.forEach(line => {
          page.drawText(line, {
              x: margin,
              y: currentY,
              font: fonts.regular,
              size: observationTextSize,
              color: colors.black,
          });
          // Move o cursor (currentY) para baixo para a próxima linha
          currentY -= lineHeight;
      });

      // Adiciona um espaçamento extra após o bloco de observação para separar da próxima seção
      currentY -= 20;
  }

  // Proposta Comercial Arasol
  page.drawRectangle({
    x: 30,
    y: currentY,
    width: width - 60,
    height: 30,
    color: colors.arasolBlue, // Cor de fundo azul
  });

  let text = '3 | Proposta Comercial ARASOL';
  page.drawText(text, {
    x: 40,
    y: currentY + 10,
    size: 14,
    font: fonts.bold,
    color: rgb(1, 1, 1), // Cor de texto branca
  });

  currentY -= 50

  page.drawRectangle({
    x: 30,
    y: currentY,
    width: width - 60,
    height: 30,
    color: colors.amareloescuro,
  });

  text = 'Valor Total do Projeto: ';
  page.drawText(text, {
    x: 40,
    y: currentY + 10,
    size: 14,
    font: fonts.bold,
  });

  text = `${parseFloat(proposta.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`;
  page.drawText(text, {
    x: 450,
    y: currentY + 10,
    size: 14,
    font: fonts.bold,
  });

  currentY -= 50

  page.drawRectangle({
    x: 30,
    y: currentY,
    width: width - 60,
    height: 20,
    color: colors.amareloclaro, // Cor de fundo azul
  });

  page.drawLine({
    start: { x: 30, y: currentY },
    end: { x: width - 30, y: currentY },
    thickness: 2,
    color: colors.azul,
  });

  text = 'Condições Comerciais';

  page.drawText(text, {
    x: 30,
    y: currentY + 5,
    size: 11,
    color: colors.azul,
    font: fonts.bold,
  });

  currentY -= 30

  text = 'Serviços serão realizados em até 72 horas úteis após o aceite da proposta, mediante agendamento prévio';
  page.drawText(text, {
    x: 30,
    y: currentY,
    size: 11,
  });

  currentY -= 15

  text = 'Caso o loca esteja fechado, sem aviso prévio do contratante, será cobrada uma tarifa adicional de R$ 50,00';
  page.drawText(text, {
    x: 30,
    y: currentY,
    size: 11,
  });

  currentY -= 15

  text = `por agendamento`;
  page.drawText(text, {
    x: 30,
    y: currentY,
    size: 11,
  });

  currentY -= 50

  page.drawRectangle({
    x: 200,
    y: currentY,
    width: 200,
    height: 20,
    color: colors.amareloescuro, // Cor de fundo azul
  });

  text = 'Proposta válida por 10 dias';
  page.drawText(text, {
    x: 215,
    y: currentY + 5,
    size: 12,
    color: colors.azul,
    font: fonts.bold,
  });

  page.drawLine({
    start: { x: 30, y: height - 745 },
    end: { x: 270, y: height - 745 },
    thickness: 1,
  });

  text = empresa?.razaoSocial || 'ARASOL Energia Fotovoltaica';

  page.drawText(text, {
    x: 90,
    y: height - 755,
    size: 10,
  });

  text = `CNPJ: ${empresa?.cnpj || '37.119.496/0001-27'}`;

  page.drawText(text, {
    x: 95,
    y: height - 765,
    size: 10,
  });

  page.drawLine({
    start: { x: 320, y: height - 745 },
    end: { x: 560, y: height - 745 },
    thickness: 1,
  });
  text = `Cliente: ${proposta.cliente.nome}`;
  page.drawText(text, {
    x: 330,
    y: height - 755,
    size: 10,
  });

  text = `CPF/CNPJ: ${proposta.cliente.cpf}`;
  page.drawText(text, {
    x: 330,
    y: height - 765,
    size: 10,
  });
};

// --- FUNÇÃO PRINCIPAL ---
const generatePdfLimpeza = async (proposta) => {
  const pdfDoc = await PDFDocument.create();
  const [width, height] = [595.28, 841.89];
  let empresa = null;
  try {
    empresa = await getEmpresaAtual();
  } catch (e) { /* sem dados de empresa — usa os fallbacks fixos */ }

  // --- Contexto compartilhado para todas as páginas ---
  const context = {
    width,
    height,
    margin: 50,
    fonts: {
      regular: await pdfDoc.embedFont(StandardFonts.Helvetica),
      bold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
    },
    colors: {
      black: rgb(0, 0, 0),
      white: rgb(1, 1, 1),
      arasolBlue: rgbHex(coresHex.chumbo), // rebrand: era o azul Arasol, agora chumbo Marcki
      grayText: rgbHex(coresHex.cinzaMedio), // rebrand: era cinza fixo da tabela
      amareloclaro: rgbHex(coresHex.laranjaClaro), // rebrand: era dourado Arasol
      amareloescuro: rgbHex(coresHex.laranja), // rebrand: era dourado Arasol
    },
    images: await loadAllImages(pdfDoc),
    empresa,
  };
  

  // --- Desenhar cada página ---
  drawPage1(pdfDoc, context, proposta);
  drawPage2(pdfDoc, context);
  drawPage3(pdfDoc, context, proposta);

  // --- Geração e Download do Arquivo ---
  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = `proposta__servicos_${proposta.cliente.nome.replace(/\s+/g, '_')}.pdf`;
  link.click();
  window.URL.revokeObjectURL(link.href);
};

export default generatePdfLimpeza;